import { useAuth0 } from "@auth0/auth0-react";
import { useState } from "react";

import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import {
  COMMON_CURRENCIES,
  COMMON_TIMEZONES,
  type UserSettings,
} from "../../lib/settings";

const NULLISH = "—";

export type ProfileSectionProps = {
  settings: UserSettings;
  onChange: (next: UserSettings) => void;
};

export function ProfileSection({ settings, onChange }: ProfileSectionProps) {
  const { user, logout } = useAuth0();
  const email = typeof user?.email === "string" ? user.email : NULLISH;
  const [saved, setSaved] = useState(false);

  const update = (patch: Partial<UserSettings["profile"]>) => {
    onChange({ ...settings, profile: { ...settings.profile, ...patch } });
    setSaved(true);
    window.setTimeout(() => {
      setSaved(false);
    }, 1500);
  };

  return (
    <section aria-labelledby="profile-heading" className="space-y-6">
      <header>
        <h2
          id="profile-heading"
          className="font-display text-xl font-medium tracking-tight text-on-surface"
        >
          Profile
        </h2>
        <p className="mt-1 text-sm text-on-surface-variant">
          Your account profile and rendering preferences. Changes save
          automatically.
        </p>
      </header>

      <div className="space-y-4">
        <Field id="profile-display-name" label="Display name">
          <Input
            id="profile-display-name"
            type="text"
            value={settings.profile.displayName}
            onChange={(event) => {
              update({ displayName: event.target.value });
            }}
            placeholder="How you'd like to be addressed"
            maxLength={120}
          />
        </Field>

        <Field id="profile-email" label="Email">
          <Input
            id="profile-email"
            type="email"
            value={email}
            disabled
            aria-readonly
          />
          <p className="mt-1 text-xs text-on-surface-variant">
            Email is sourced from your Auth0 identity and can&apos;t be
            edited here.
          </p>
        </Field>

        {/* TODO(phase-6b): avatar upload. Needs an attachments-style
            adapter for user-profile images; deferred until we land a
            durable backend store + bytes-cap policy. */}

        <Field id="profile-timezone" label="Timezone">
          <select
            id="profile-timezone"
            value={settings.profile.timezone}
            onChange={(event) => {
              update({ timezone: event.target.value });
            }}
            className="flex h-9 w-full rounded-md border border-outline bg-surface-container px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus-ring"
          >
            {(COMMON_TIMEZONES.includes(settings.profile.timezone)
              ? COMMON_TIMEZONES
              : [settings.profile.timezone, ...COMMON_TIMEZONES]
            ).map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </Field>

        <Field id="profile-currency" label="Base currency">
          <select
            id="profile-currency"
            value={settings.profile.baseCurrency}
            onChange={(event) => {
              update({ baseCurrency: event.target.value });
            }}
            className="flex h-9 w-full rounded-md border border-outline bg-surface-container px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-focus-ring"
          >
            {COMMON_CURRENCIES.map((ccy) => (
              <option key={ccy} value={ccy}>
                {ccy}
              </option>
            ))}
          </select>
        </Field>

        <div aria-live="polite" className="h-5 text-xs text-on-surface-muted">
          {saved ? "Saved." : ""}
        </div>
      </div>

      <div className="border-t border-outline-variant pt-4">
        <Button
          type="button"
          variant="outline"
          onClick={() => {
            void logout({ logoutParams: { returnTo: window.location.origin } });
          }}
        >
          Log out
        </Button>
      </div>
    </section>
  );
}

function Field({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-1 gap-1 sm:grid-cols-[180px_1fr] sm:items-start sm:gap-4">
      <label
        htmlFor={id}
        className="pt-1.5 text-sm font-medium text-on-surface"
      >
        {label}
      </label>
      <div className="min-w-0">{children}</div>
    </div>
  );
}
